import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { useCallback, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { EmailComposerContext, IEmailComposerContext } from '../../../../models/Email';
import {
	AI_CAMPAIGN_MINIMUM_LEAD_HOURS,
	earliestAICampaignSend,
	IAICampaignBrief,
	IAICampaignSendFrom,
} from '../../../../models/AICampaign';
import { getDefaultBulkMessagingBodyEditorState } from '../../../../models/UiUtils';
import { useFullscreenModal, useUserSession } from '../../../../models/hooks/appStateHooks';
import { useEmailUpdateSendScheduleMutation } from '../../../../queries';
import { useAICampaignQuota, useCreateAICampaign } from '../../../../queries/AICampaign';
import { ComposeEmailViewModel } from '../../../../viewmodels/AppViewModels';
import { MultiContainerHeader } from '../../../components/MultiContainerHeader';
import { SendFromDropdown } from '../../../components/SendFromDropdown';
import { TagEmailRecipientsList } from '../../../components/email/TagEmailRecipientsList';
import { EditAICampaignBrief } from '../EditAICampaignBrief';
import { EditSendOptions } from '../EditSendOptions';
import { Search } from '../Search';
import { styleSheet } from './styles';

type Step = 'audience' | 'recipients' | 'schedule';

/**
 * The same shell as an ordinary campaign: the brief on the left where the composer would be, the standard recipient
 * picker and preview on the right, then the standard send-options screen. Generation is not part of this flow: a
 * recurring job writes each recipient's email roughly a day and a half before the send, so the research is current
 * rather than as old as the scheduling decision, and the content lands before the day-before notification so that
 * becomes the prompt to review it.
 */
export const CreateAICampaign: React.FC = () => {
	const userSession = useUserSession();
	const fullscreenModal = useFullscreenModal();

	// The composer is only the vehicle that lets the shared recipient picker, preview and scheduler operate; its
	// content is never sent. The generated emails are written per recipient after the campaign is scheduled.
	const composerRef = useRef<ComposeEmailViewModel>(null);
	if (!composerRef.current) {
		const composer = new ComposeEmailViewModel(userSession);
		composer.emailMessage = new Api.EmailMessageViewModel<File, Api.ISendEmailResponse>(userSession);
		composer.emailMessage.content = getDefaultBulkMessagingBodyEditorState().getRawRichTextContent();
		// There is no email to customize per recipient yet; each one is written after the campaign is scheduled.
		composer.canCustomizeIndividualEmails = false;
		composerRef.current = composer;
	}
	const emailComposer = composerRef.current;

	// No sendTestEmail on purpose: there is nothing to test-send before generation, and Search hides its
	// "Send a Test" affordance when the context does not provide one.
	const composerContext = useMemo<IEmailComposerContext>(() => ({ emailComposer }), [emailComposer]);

	const [step, setStep] = useState<Step>('audience');
	const [brief, setBrief] = useState<IAICampaignBrief | null>(null);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [sendFromOption, setSendFromOption] = useState<Api.ISendOnBehalfPermission | undefined>();
	const [sendFromUser, setSendFromUser] = useState<Api.IUser | undefined>();
	const [sendFromEmailAddress, setSendFromEmailAddress] = useState<string | undefined>();

	// The picker only records the choice here; the composer carries it so the recipient preview resolves against the
	// same ownership filter the send will use. Sending as someone else invalidates a campaign already created from a
	// different sender, which the created-campaign fingerprint below accounts for.
	const sendOnBehalfOf = (option?: Api.ISendOnBehalfPermission, user?: Api.IUser) => {
		setSendFromOption(option);
		setSendFromUser(option?.sender?.mode === Api.SendEmailFrom.SelectedUser ? user : undefined);

		const options = emailComposer.emailMessage?.options;
		if (options) {
			options.sendEmailFrom = option?.sender?.mode;
			options.sendFromConnectionType = option?.sender?.connectionType;
			emailComposer.emailMessage.setSendEmailFromUser(
				option?.sender?.mode === Api.SendEmailFrom.SelectedUser ? user : undefined
			);
		}

		const request = emailComposer.emailMessage?.contactsFilterRequest;
		if (request) {
			if (option?.requiredFilter) {
				request.ownershipFilter = option.requiredFilter;
			} else if (option?.sender?.mode !== Api.SendEmailFrom.CurrentUser) {
				request.ownershipFilter = null;
			}
		}

		emailComposer.resetRecipientsResultsList();
	};

	// Creating and scheduling are two calls, so a scheduling failure leaves a real, unscheduled campaign behind.
	// Remembering what it was created from means retrying schedules that campaign, while any edit to the brief or
	// audience creates a fresh one rather than scheduling a stale campaign.
	const createdCampaign = useRef<{ campaignId: string; briefJson: string; audienceJson: string } | null>(null);

	const onBriefChange = useCallback((next: IAICampaignBrief | null) => {
		setBrief(prev => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
	}, []);

	const scheduleMutation = useEmailUpdateSendScheduleMutation({
		onError: error => {
			setIsSubmitting(false);
			toast.error(error?.systemMessage || 'Could not schedule this campaign');
		},
		onSuccess: () => {
			toast.success('Campaign scheduled. You will hear from us when the emails are written.');
			fullscreenModal?.dismissModal();
		},
	});

	const createMutation = useCreateAICampaign({
		onError: error => {
			setIsSubmitting(false);
			toast.error(error?.systemMessage || 'Could not create this campaign');
		},
	});

	// Read for the send window it carries. Absent while it loads, which earliestSendDate falls back from, so the
	// picker is bounded by the presented two days either way rather than briefly unbounded.
	const { data: quota } = useAICampaignQuota();
	const earliestSend = earliestAICampaignSend(quota);

	// The picker embeds NOT criteria for the system exclusion tags, but the AI campaign API applies those exclusions
	// itself whatever arrives, so sending them along only doubles them up in the stored audience.
	const isServerAppliedExclusion = (criterion: Api.IContactFilterCriteria) =>
		criterion.op === Api.FilterOperator.Not &&
		criterion.property === Api.ContactFilterCriteriaProperty.Tag &&
		Api.DefaultBulkSendExcludedTags.has(criterion.value);

	// The picker writes the audience onto the composer the same way the ordinary flow consumes it. The AI campaign API
	// takes one contact filter, so the criteria are folded into a single implicitly-ANDed filter, keeping the created
	// audience identical to what the preview showed. Ownership is NOT folded in: it travels as its own field, because
	// the server reads it separately and treats an ownership filter of All as no filter, so appending it as a
	// criterion silently discarded the restriction.
	const audienceFromComposer = (): Api.ContactFilter | null => {
		const filter = emailComposer.emailMessage?.contactsFilterRequest?.contactFilterRequest;

		const criteria: Api.IContactFilterCriteria[] = [];
		if (filter?.property != null || filter?.value != null) {
			criteria.push(filter);
		} else if (filter?.criteria?.length) {
			if (filter.op && filter.op !== Api.FilterOperator.And) {
				criteria.push(filter);
			} else {
				criteria.push(...filter.criteria.filter(criterion => !isServerAppliedExclusion(criterion)));
			}
		}

		return criteria.length > 0 ? { criteria, op: Api.CONTACT_FILTER_OPERATOR.And } : null;
	};

	// Whichever ownership scope the picker resolved. Preferring the explicit filter over the calculated one matches
	// how the ordinary flow reads it, and sending it verbatim lets the server decide whether it narrows anything.
	const ownershipFromComposer = (): Api.IOwnershipFilter | undefined => {
		const request = emailComposer.emailMessage?.contactsFilterRequest;
		return request?.ownershipFilter ?? request?.calculatedOwnershipFilter ?? undefined;
	};

	const contactIdsFromComposer = (): string[] =>
		(emailComposer.emailMessage?.contactsToAdd?.map(contact => contact.id) ?? []).filter(Boolean);

	// Returns whether the flow may advance, rather than the audience itself: an audience of only individually chosen
	// contacts has no filter, so a null filter can no longer stand for "not ready".
	const guardBriefAndAudience = (): boolean => {
		if (!brief) {
			toast.error('Describe what this campaign should accomplish before continuing.');
			return false;
		}

		// A filter, individually chosen contacts, or both: either half is a complete audience on its own.
		if (!audienceFromComposer() && contactIdsFromComposer().length === 0) {
			toast.error('Choose who this campaign is for.');
			return false;
		}

		return true;
	};

	const schedule = async (sendAt: Date) => {
		if (isSubmitting) {
			return;
		}

		if (!guardBriefAndAudience()) {
			return;
		}

		const contactIds = contactIdsFromComposer();
		const audience = audienceFromComposer();

		setIsSubmitting(true);

		// Created and scheduled as one user action. The campaign has no content yet, which is the point: it is written
		// closer to the send.
		const sendFrom: IAICampaignSendFrom = {
			connectionType: sendFromOption?.sender?.connectionType,
			mode: sendFromOption?.sender?.mode ?? Api.SendEmailFrom.CurrentUser,
			selectedUserId: sendFromUser?.id,
			sendFromEmailAddress,
		};
		const excludedContactIds = (emailComposer.emailMessage?.contactsToOmit?.map(contact => contact.id) ?? []).filter(
			Boolean
		);
		const ownership = ownershipFromComposer();
		const briefJson = JSON.stringify(brief);
		const audienceJson = JSON.stringify({ audience, contactIds, excludedContactIds, ownership, sendFrom });
		let campaignId =
			createdCampaign.current?.briefJson === briefJson && createdCampaign.current?.audienceJson === audienceJson
				? createdCampaign.current.campaignId
				: null;
		if (!campaignId) {
			const campaign = await createMutation
				.mutateAsync({
					audience,
					brief,
					contactIds,
					excludedContactIds,
					name: briefToName(brief),
					ownership,
					sendFrom,
				})
				.catch((): Api.ICampaign | null => null);
			if (!campaign?.id) {
				setIsSubmitting(false);
				return;
			}

			campaignId = campaign.id;
			createdCampaign.current = { audienceJson, briefJson, campaignId };
		}

		scheduleMutation.mutate({
			campaignId,
			schedule: { criteria: Api.ScheduleCriteria.StartAfter, startDate: sendAt.toISOString() },
			timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
		});
	};

	const onScheduleCtaClicked = () => {
		const scheduledSend = emailComposer.emailMessage?.options?.scheduledSend;
		const startDate = scheduledSend?.startDate ? new Date(scheduledSend.startDate) : null;
		if (!startDate || scheduledSend?.criteria === Api.ScheduleCriteria.Immediately) {
			toast.error('Pick a send date for this campaign.');
			return;
		}

		// Belt and braces. The picker is bounded by the same date, so this only catches a stale selection, but the
		// alternative to keeping it is trusting the picker to be the only way a date reaches here.
		if (startDate < earliestSend) {
			toast.error(
				`Choose a time at least ${AI_CAMPAIGN_MINIMUM_LEAD_HOURS} hours out, which leaves time to write the emails and for you to review them.`
			);
			return;
		}

		schedule(startDate);
	};

	// preventDefault, or the modal's default back fires too and pops its history out from under the wizard. On the
	// first step there is no custom handler, so that default takes over and leaves the flow. Close needs no handler:
	// the modal's default already dismisses it.
	const onHeaderBack =
		step === 'audience'
			? undefined
			: (e: React.MouseEvent<HTMLElement>) => {
					e.preventDefault();
					setStep(step === 'schedule' ? 'recipients' : 'audience');
				};

	return (
		<div className={css(styleSheet.container)}>
			<MultiContainerHeader fullscreenHeader='Create AI Email Campaign' onFullscreenRequestBack={onHeaderBack} />
			{step === 'schedule' ? (
				<div className={css(styleSheet.sendOptionsContainer)}>
					<EditSendOptions
						disableSendNow={true}
						emailComposer={emailComposer}
						initialStartDate={earliestSend}
						minimumStartDate={earliestSend}
						onScheduleCtaClicked={onScheduleCtaClicked}
						requireEstimateToSend={false}
						submitButtonText={isSubmitting ? 'Scheduling...' : 'Schedule Campaign'}
					/>
				</div>
			) : (
				<>
					<div className={css(styleSheet.leftContainer)}>
						<div className={css(styleSheet.sendFromRow)}>
							<SendFromDropdown
								allowedContactBasedOptions='all'
								canSendOnBehalf={emailComposer.canSendOnBehalf}
								sendFromEmailAddress={sendFromEmailAddress}
								sendFromOption={sendFromOption}
								sendFromUserId={sendFromUser?.id}
								sendOnBehalfOf={sendOnBehalfOf}
								onSendFromEmailAddressChange={setSendFromEmailAddress}
							/>
						</div>
						<div className={css(styleSheet.briefCard)}>
							<EditAICampaignBrief brief={brief ?? undefined} onBriefChange={onBriefChange} />
						</div>
					</div>
					<EmailComposerContext.Provider value={composerContext}>
						<div className={css(styleSheet.contextContainer)}>
							{step === 'audience' ? (
								<Search
									onNextClick={() => {
										if (guardBriefAndAudience()) {
											setStep('recipients');
										}
									}}
								/>
							) : (
								// The list and its CTA are siblings in a fragment; this wrapper is the flex column that keeps
								// the CTA pinned on screen while the list scrolls.
								<div className={css(styleSheet.recipientsContainer)}>
									<TagEmailRecipientsList
										ctaButtonText='Next: Send Options'
										// Individuals can be added as well as struck: they travel as contact ids rather than
										// through the filter, and the server unions them with whatever the filter selects.
										// Editing is still off, because there is no email to customize before generation.
										disableContactEdits={true}
										emailComposer={emailComposer}
										onCtaClick={() => {
											if (guardBriefAndAudience()) {
												setStep('schedule');
											}
										}}
										styles={[styleSheet.recipientsList]}
									/>
								</div>
							)}
						</div>
					</EmailComposerContext.Provider>
				</>
			)}
		</div>
	);
};

// The goal's opening words make a more recognizable campaign name in reporting than a generic label. Reporting
// shows this name where a subject line would go, so it gets a capital letter and an ellipsis when clipped.
const briefToName = (brief: IAICampaignBrief) => {
	const words = brief.goal.trim().split(/\s+/).filter(Boolean);
	if (words.length === 0) {
		return 'AI campaign';
	}

	const clipped = words.slice(0, 8).join(' ');
	return clipped.charAt(0).toUpperCase() + clipped.slice(1) + (words.length > 8 ? '…' : '');
};
